/**
 * dataHealth.ts — response-SHAPE schemas for the DATA-HEALTH bucket (fetchEnvelope's optional gate).
 * Two endpoints back the honesty layer:
 *   - GET /api/v1/data-health         -> DataHealthRow[]  (per-model manifest; DataHealth page + shell banner)
 *   - GET /api/v1/data-health/matrix  -> MatrixTile[]     (Real-vs-Mock matrix page)
 *
 * These follow the LOCKED leniency conventions in ./base (structural drift only, never value-policing):
 *   - `| null`   => .nullable()      - optional `?` => .optional()      - both => .nullable().optional()
 *   - ONLY primitives (z.string()/z.number()/z.boolean()); NO z.enum()/z.literal() — MatrixTile.status is a
 *     "REAL|PARTIAL|MOCK|DEFERRED" union in the contract but is modeled as plain z.string() so the server
 *     can add a status without a false-positive rejection; the render (Matrix StatusBadge) already falls
 *     back to a neutral color for an unknown status.
 *   - NO .strict() — objects allow unknown/extra server keys (forward-compatible; the gate returns the
 *     ORIGINAL payload untouched).
 * The gate catches STRUCTURAL drift (array vs non-array, a missing required field, a wrong primitive) at
 * the boundary so it surfaces as a handled ApiShapeError (page PageError / chrome ErrorBoundary) instead of
 * throwing deep in render.
 */
import { z } from "zod";
import type { DataHealthRow, MatrixTile } from "@shared/contracts";
import { provenancedRowShape, type AssertExtends } from "./base";

/**
 * Data-health manifest row (#33, `DataHealthRow` extends `ProvenancedRow`) — the honesty layer's
 * self-report. Spreads the shared provenanced-row columns (data_status / provenance_feed / gap_reason);
 * model_name is required-but-nullable and row_count is required-but-nullable per the contract.
 */
const dataHealthRowSchema = z.object({
  ...provenancedRowShape,
  model_name: z.string().nullable(),
  // row_count is `number | null` in the contract, but the honesty layer's DataHealthBanner is ALWAYS
  // mounted app-wide and must NEVER false-fail. The LIVE derive path casts count(*)::int (a JS number),
  // but the latent vw_data_health VIEW path returns row_count UNCAST and node-postgres serializes a
  // Postgres bigint as a STRING — which a strict z.number() would reject, throw ApiShapeError, and flip
  // the banner to "unavailable" everywhere. Accept BOTH (leniency, NOT value-policing) so the always-
  // honest layer tolerates the bigint-as-string view path defensively. DURABLE FIX (not this round): add
  // a server-side row_count::int cast in the data-health MANIFEST_SQL so the wire type is always a number.
  row_count: z.union([z.number(), z.string()]).nullable(),
});
// Contract row_count (number | null) is assignable to number | string | null, so the link still holds.
type _DataHealthRowLink = AssertExtends<DataHealthRow, z.infer<typeof dataHealthRowSchema>>;

/** GET /api/v1/data-health payload — the manifest ARRAY (array-vs-non-array is the key drift this guards). */
export const dataHealthRowsSchema = z.array(dataHealthRowSchema) as unknown as z.ZodType<DataHealthRow[]>;

/**
 * Data-health matrix tile (#34, `MatrixTile` — does NOT extend ProvenancedRow). tile/backing_model are
 * required strings; unblocking_feed and data_status are required-but-nullable; status is the
 * REAL|PARTIAL|MOCK|DEFERRED union collapsed to z.string() (rule 3 — never value-police).
 */
const matrixTileSchema = z.object({
  tile: z.string(),
  backing_model: z.string(),
  unblocking_feed: z.string().nullable(),
  status: z.string(),
  data_status: z.string().nullable(),
});
type _MatrixTileLink = AssertExtends<MatrixTile, z.infer<typeof matrixTileSchema>>;

/** GET /api/v1/data-health/matrix payload — the tile ARRAY. */
export const matrixTilesSchema = z.array(matrixTileSchema) as unknown as z.ZodType<MatrixTile[]>;
